import { ReactElement, useState } from 'react';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { Tooltip } from '@material-ui/core';
import classNames from 'classnames';

import { getMeasurementData } from '../helper';
import { isoToFormat } from '~/utils/date';

import { Timezone } from '~/types';
import { INotificationRule } from '~/services/api/notificationRules/types';
import { IBloodPressureReading } from '~/services/api/readings/types';
import styles from './BloodPressure.module.scss';
import Icon from './icons/BloodPressure.svg';
import { RuleType } from '~/services/api/enums';
import BloodPressureTooltip from './BloodPressureTooltip';
import api from '~/services/api';
import { useStoreActions } from '~/store/hooks';
import { extractErrorMessage } from '~/utils/error';
import { IBloodPressureReadingUpdate } from '~/services/api/bloodPressureReadings/types';
import { POSITION_ICONS_MAP } from './helpers';
import { ReactComponent as MedicationNotTaken } from './icons/MedicationNotTaken.svg';

interface IProps {
  reading: IBloodPressureReading;
  rules: INotificationRule[];
  timezone: Timezone;
  isHighlighted?: boolean;
  isEdit?: boolean;
  onUpdated: (data: IBloodPressureReading) => void;
}

const BloodPressure = ({
  reading,
  rules,
  timezone,
  isHighlighted,
  isEdit = false,
  onUpdated,
}: IProps): ReactElement => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [isToolTipOpen, setIsToolTipOpen] = useState(false);

  const { showNotify, showError } = useStoreActions(actions => actions.snackbar);

  const updateBloodPressureReading = async (data: IBloodPressureReadingUpdate) => {
    try {
      if (isUpdating) {
        return;
      }

      setIsUpdating(true);

      const newReading = await api.bloodPressureReadings.update(data).then(x => x.data);

      onUpdated(newReading);

      showNotify('Blood Pressure Reading successfully updated');
    } catch (e) {
      showError(extractErrorMessage(e));
    }
    setIsUpdating(false);
  };

  const systolicData = getMeasurementData({
    typeId: RuleType.Systolic,
    value: reading.systolic,
    rules,
  });
  const diastolicData = getMeasurementData({
    typeId: RuleType.Diastolic,
    value: reading.diastolic,
    rules,
  });
  const heartRateData = getMeasurementData({
    typeId: RuleType.HeartRate,
    value: reading.pulse,
    rules,
  });

  const measurementDate = `${isoToFormat(reading.measuredAt, 'dd MMM y HH:mm')} ${timezone}`;

  const receivedBySystem = `Received by system: ${isoToFormat(
    reading.createdAt,
    'dd MMM y HH:mm',
  )} ${timezone}`;

  const PositionIcon = POSITION_ICONS_MAP[reading.positionTypeId];

  return (
    <Tooltip
      open={(isToolTipOpen || isUpdating) && isEdit}
      onOpen={() => setIsToolTipOpen(true)}
      onClose={() => !isUpdating && setIsToolTipOpen(false)}
      interactive
      disableFocusListener
      arrow
      placement="bottom"
      title={
        isEdit && <BloodPressureTooltip reading={reading} onUpdate={updateBloodPressureReading} />
      }
      classes={{
        tooltip: styles.customTooltip,
        arrow: styles.customArrow,
      }}
    >
      <div className={classNames(styles.container, { [styles.isHighlighted]: isHighlighted })}>
        <div className={styles.icon}>
          <Tooltip title="Blood Pressure">
            <img src={Icon} alt="" />
          </Tooltip>
        </div>
        <div className={styles.info}>
          <div className={styles.measurements}>
            <div className={systolicData.className}>
              <span className={styles.value}>{systolicData.value}</span>
            </div>
            <div className={diastolicData.className}>
              <span className={styles.value}>{diastolicData.value}</span>
            </div>
            <div className={heartRateData.className}>
              <span className={styles.value}>{heartRateData.value}</span>
            </div>
          </div>
          <div className={styles.date} title={receivedBySystem}>
            {measurementDate}
          </div>
        </div>
        {reading.irregularHeartbeat && (
          <div className={styles.statusIcon}>
            <Tooltip title="Irregular heartbeat">
              <FavoriteIcon className={styles.heartBeatIcon} />
            </Tooltip>
          </div>
        )}
        {reading.positionTypeId && (
          <div className={styles.statusIcon}>
            <Tooltip title="Body Position">
              <PositionIcon width={20} height={20} />
            </Tooltip>
          </div>
        )}
        {reading.isMedicationNotTaken && (
          <div className={styles.statusIcon}>
            <Tooltip title="Medication">
              <MedicationNotTaken width={25} height={25} />
            </Tooltip>
          </div>
        )}
      </div>
    </Tooltip>
  );
};

export default BloodPressure;
